import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

const mixpanelTokenSchema =
  process.env.NEXT_PUBLIC_ENV === 'prod'
    ? z
        .string()
        .min(1)
        .catch((err) => {
          console.error(
            'NEXT_PUBLIC_MIXPANEL_TOKEN validation failed:',
            process.env.NEXT_PUBLIC_MIXPANEL_TOKEN,
            err
          )
          throw err
        })
    : z.string().optional()

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    NODE_ENV: z.enum(['development', 'test', 'production']),
    GOOGLE_CLIENT_ID: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    ELEVENLABS_API_KEY: z.string(),
  },
  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_API_URL: z
      .string()
      .url()
      .catch((err) => {
        console.error(
          'NEXT_PUBLIC_API_URL validation failed:',
          process.env.NEXT_PUBLIC_API_URL,
          err
        )
        throw err
      }),
    NEXT_PUBLIC_WS_URL: z
      .string()
      .url()
      .catch((err) => {
        console.error('NEXT_PUBLIC_WS_URL validation failed:', process.env.NEXT_PUBLIC_WS_URL, err)
        throw err
      }),
    NEXT_PUBLIC_ENV: z.enum(['staging', 'prod', 'dev']).catch((err) => {
      console.error('NEXT_PUBLIC_ENV validation failed:', process.env.NEXT_PUBLIC_ENV, err)
      throw err
    }),
    NEXT_PUBLIC_BASE_URL: z
      .string()
      .url()
      .catch((err) => {
        console.error(
          'NEXT_PUBLIC_BASE_URL validation failed:',
          process.env.NEXT_PUBLIC_BASE_URL,
          err
        )
        throw err
      }),
    NEXT_PUBLIC_MIXPANEL_TOKEN: mixpanelTokenSchema,
    NEXT_PUBLIC_BRANDFETCH_CLIENT_ID: z.string().catch((err) => {
      console.error(
        'NEXT_PUBLIC_BRANDFETCH_CLIENT_ID validation failed:',
        process.env.NEXT_PUBLIC_BRANDFETCH_CLIENT_ID,
        err
      )
      throw err
    }),
    NEXT_PUBLIC_AI_URL: z
      .string()
      .url()
      .catch((err) => {
        console.error('NEXT_PUBLIC_AI_URL validation failed:', process.env.NEXT_PUBLIC_AI_URL, err)
        throw err
      }),
    NEXT_PUBLIC_TV_LIBRARY_PATH: z.string().url().optional(),
    // Cloudflare Turnstile site key; unset (local dev) skips the bot check entirely.
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().optional(),
    NEXT_PUBLIC_INTERCOM_APP_ID: z.string().catch((err) => {
      console.error(
        'NEXT_PUBLIC_INTERCOM_APP_ID validation failed:',
        process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
        err
      )
      throw err
    }),
    NEXT_PUBLIC_REOWN_PROJECT_ID: z.string().catch((err) => {
      console.error(
        'NEXT_PUBLIC_REOWN_PROJECT_ID validation failed:',
        process.env.NEXT_PUBLIC_REOWN_PROJECT_ID,
        err
      )
      throw err
    }),
    // The deploy workflow passes GITHUB_SHA; a stable 'dev' sentinel everywhere
    // else (local dev, bun test) so the client/server build-freshness check
    // never false-positives outside a real multi-deploy prod environment.
    NEXT_PUBLIC_BUILD_ID: z.string().default('dev'),
  },
  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_WS_URL: process.env.NEXT_PUBLIC_WS_URL,
    NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
    NEXT_PUBLIC_MIXPANEL_TOKEN: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN,
    NEXT_PUBLIC_BRANDFETCH_CLIENT_ID: process.env.NEXT_PUBLIC_BRANDFETCH_CLIENT_ID,
    ELEVENLABS_API_KEY: process.env.ELEVENLABS_API_KEY,
    NEXT_PUBLIC_AI_URL: process.env.NEXT_PUBLIC_AI_URL,
    NEXT_PUBLIC_TV_LIBRARY_PATH: process.env.NEXT_PUBLIC_TV_LIBRARY_PATH,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
    NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
    NEXT_PUBLIC_REOWN_PROJECT_ID: process.env.NEXT_PUBLIC_REOWN_PROJECT_ID,
    NEXT_PUBLIC_BUILD_ID: process.env.NEXT_PUBLIC_BUILD_ID,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
   * This is especially useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
})
